/* eslint-disable @typescript-eslint/no-magic-numbers */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { FirebaseError } from 'firebase/app';
import { filter, first, tap } from 'rxjs';
import { password, sendVerificationMail, verifyOOBCode } from '../../actions/auth.actions';
import { localizationKeys } from '../../constants/localizationKeys';
import { FirebaseErrorCode } from '../../enums/firebase-error-code.enum';
import { ValidatorsHelper } from '../../helpers/validators.helper';
import type { IPasswords } from '../../interfaces/passwords.interface';
import type { ISetOrResetPasswordRequest } from '../../interfaces/set-or-reset-password-request.interface';
import { AuthProvider } from '../../provider/auth.provider';

const RESET_PASSWORD = 'password-reset';
@UntilDestroy()
@Component({
    selector: 'aumico-users-reset-password-page',
    templateUrl: './reset-password.page.html',
    styleUrls: ['./reset-password.page.scss'],
})
export class ResetPasswordPage implements OnInit {
    private passwordFormValue: IPasswords | undefined;
    private email: string;
    private oobCode: string;
    isPasswordFormValid = false;
    backendError = false;
    localizations = localizationKeys;
    isResetPasswordUrl: boolean;
    isOOBCodeExpiredOrInvalid: boolean;
    form: UntypedFormGroup;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    questionButtonTextKey = localizationKeys.aumico_reports_registration_confirmation_bottom_question_button;
    @Input() showTermsAndSubscriptionBoxes: boolean;
    @Output() resetPasswordSuccess = new EventEmitter<void>();
    @Output() resendVerificationMail = new EventEmitter<string>();
    @Output() passwordSet = new EventEmitter<ISetOrResetPasswordRequest>();
    @Output() pricesClicked = new EventEmitter<void>();
    @Output() termsAndConditionsClicked = new EventEmitter<void>();
    @Output() privacyPolicyClicked = new EventEmitter<void>();
    @Output() agreementOnOrderDataProcessingClicked = new EventEmitter<void>();

    constructor(
        private readonly authProvider: AuthProvider,
        private readonly actionsListener$: ActionsSubject,
        private readonly activatedRoute: ActivatedRoute,
        private readonly formBuilder: UntypedFormBuilder,
    ) {
        const urlFirstSlug = this.activatedRoute.snapshot.url[0];
        this.isResetPasswordUrl = urlFirstSlug.path.includes(RESET_PASSWORD);
        this.initializeForm();
    }
    private initializeForm(): void {
        this.form = this.formBuilder.group(
            {
                password: [
                    null,
                    {
                        validators: [Validators.required, ValidatorsHelper.checkPasswordStrength()],
                    },
                ],
                repeatedPassword: [
                    null,
                    {
                        validators: [Validators.required],
                    },
                ],
            },
            { validators: ValidatorsHelper.validateValueMatch('password', 'repeatedPassword') },
        );
    }

    ngOnInit(): void {
        if (this.showTermsAndSubscriptionBoxes) {
            this.form = this.formBuilder.group(
                {
                    password: [
                        null,
                        {
                            validators: [Validators.required, ValidatorsHelper.checkPasswordStrength()],
                        },
                    ],
                    repeatedPassword: [
                        null,
                        {
                            validators: [Validators.required],
                        },
                    ],
                    acceptPolicy: [true, { validators: [Validators.requiredTrue] }],
                    subscribeMarketing: [false],
                },
                { validators: ValidatorsHelper.validateValueMatch('password', 'repeatedPassword') },
            );
        }
        this.activatedRoute.queryParamMap
            .pipe(
                filter((queryParamMap) => {
                    const email = queryParamMap.get('email');
                    const oobCode = queryParamMap.get('oobCode');

                    return email != null && email.length !== 0 && oobCode != null && oobCode.length !== 0;
                }),
                tap((queryParamMap) => {
                    const email = queryParamMap.get('email');
                    if (email == null || email.length === 0) {
                        throw new Error("Email doesn't exist in params");
                    }
                    this.email = email;

                    const oobCode = queryParamMap.get('oobCode');
                    if (oobCode == null || oobCode.length === 0) {
                        throw new Error("oobCode doesn't exist in params");
                    }
                    this.oobCode = oobCode;
                    this.authProvider.dispatchVerifyOOB({ oobCode: this.oobCode, email: this.email });
                }),
                first(),
            )
            .subscribe();
        this.actionsListener$.pipe(ofType(verifyOOBCode.success), untilDestroyed(this)).subscribe(({ payload }) => {
            if (this.email != null && payload !== this.email) {
                this.isOOBCodeExpiredOrInvalid = true;
            }
        });
        this.actionsListener$.pipe(ofType(verifyOOBCode.failure), untilDestroyed(this)).subscribe(({ payload }) => {
            if (this.isFireBaseError(payload) && this.isInvalidErrorCode(payload as FirebaseError)) {
                this.isOOBCodeExpiredOrInvalid = true;
            } else {
                this.onFailure();
            }
        });

        this.actionsListener$
            .pipe(ofType(password.setSuccess), untilDestroyed(this))
            .subscribe(() => this.onSetOrResetPasswordSuccessfully());

        this.actionsListener$.pipe(ofType(password.setFailed), untilDestroyed(this)).subscribe((error) => {
            if (this.isFireBaseError(error.payload) && this.isInvalidErrorCode(error.payload as FirebaseError)) {
                this.isOOBCodeExpiredOrInvalid = true;
            } else {
                this.onFailure();
            }
        });
        this.actionsListener$.pipe(ofType(sendVerificationMail.success), untilDestroyed(this)).subscribe(() => {
            this.onResendVerificationMailSuccess();
        });
        this.actionsListener$
            .pipe(ofType(sendVerificationMail.failure), untilDestroyed(this))
            .subscribe(() => this.onFailure());
    }

    public openPricesLink(): void {
        this.pricesClicked.emit();
    }

    public openTermsAndConditionsLink(): void {
        this.termsAndConditionsClicked.emit();
    }

    public openPrivacyPolicyLink(): void {
        this.privacyPolicyClicked.emit();
    }

    public openAgreementOnOrderDataProcessingLink(): void {
        this.agreementOnOrderDataProcessingClicked.emit();
    }

    onValidPassword(passwordValue: IPasswords): void {
        this.passwordFormValue = passwordValue;
    }

    onPasswordFormStatusChange(isPasswordFormValid: boolean): void {
        this.isPasswordFormValid = isPasswordFormValid;
    }

    onResetPassword(): void {
        const password = this.passwordFormValue?.password;
        if (password == null) {
            throw new Error('The password in empty');
        }
        this.backendError = false;
        let setOrResetPasswordData: ISetOrResetPasswordRequest = {
            email: this.email,
            password: this.passwordFormValue.password,
            oobCode: this.oobCode,
        };

        if (this.showTermsAndSubscriptionBoxes) {
            setOrResetPasswordData = {
                ...setOrResetPasswordData,
                acceptPolicy: this.form.get('acceptPolicy').value as boolean,
                subscribeMarketing: this.form.get('subscribeMarketing').value as boolean,
            };
        }

        this.passwordSet.emit(setOrResetPasswordData);
    }

    resendVerificationMailClicked(): void {
        this.authProvider.dispatchResendVerificationMail(this.email);
    }
    private onSetOrResetPasswordSuccessfully(): void {
        this.resetPasswordSuccess.emit();
    }
    private onFailure(): void {
        this.backendError = true;
    }

    private onResendVerificationMailSuccess(): void {
        this.resendVerificationMail.emit(this.email);
    }

    private isFireBaseError(error: Error | FirebaseError | HttpErrorResponse): boolean {
        return error instanceof FirebaseError;
    }

    private isInvalidErrorCode(error: FirebaseError): boolean {
        return [FirebaseErrorCode.INVALID_ACTION_CODE, FirebaseErrorCode.EXPIRED_ACTION_CODE].includes(
            error.code as FirebaseErrorCode,
        );
    }
}
